import { apiClient } from './api';
import { Position } from '../types';

interface PositionsResponse {
  sessionId: string;
  positions: Position[];
  count: number;
  lastKey?: string;
  hasMore: boolean;
}

class PositionsService {
  private endpoint = '/admin/positions';

  /**
   * Fetch all positions for a specific session with pagination support
   * Recursively fetches all pages if needed
   */
  async getPositionsBySessionId(
    sessionId: string,
    options?: {
      limit?: number;
      maxPages?: number;
    }
  ): Promise<Position[]> {
    try {
      const limit = options?.limit || 1000;
      const maxPages = options?.maxPages || 10; // Safety limit

      let allPositions: Position[] = [];
      let lastKey: string | undefined;
      let pageCount = 0;
      let response: PositionsResponse;

      do {
        pageCount++;

        // Build URL with pagination
        const url = `${this.endpoint}?sessionId=${sessionId}&limit=${limit}${
          lastKey ? `&lastKey=${encodeURIComponent(lastKey)}` : ''
        }`;

        response = (await apiClient.get<PositionsResponse>(url)).data as PositionsResponse;

        // Accumulate positions
        allPositions = [...allPositions, ...response.positions];

        // Prepare next page key
        lastKey = response.lastKey;

        // Logging
        console.log(
          `[PositionsService] Page ${pageCount}: ${response.count} positions, hasMore: ${response.hasMore}`
        );

        // Safety limit
        if (pageCount >= maxPages) {
          console.warn(
            `[PositionsService] Reached max pages limit (${maxPages}), stopping pagination`
          );
          break;
        }
      } while (lastKey && response.hasMore);

      console.log(
        `[PositionsService] Total: ${allPositions.length} positions retrieved in ${pageCount} pages`
      );

      return allPositions;
    } catch (error) {
      console.error('Error fetching positions:', error);
      throw error;
    }
  }

  /**
   * Fetch only the first page of positions (optimized for live map)
   * Returns latest positions without full pagination
   */
  async getLatestPositions(
    sessionId: string,
    limit: number = 100
  ): Promise<Position[]> {
    try {
      const response = await apiClient.get<PositionsResponse>(
        `${this.endpoint}?sessionId=${sessionId}&limit=${limit}`
      );
      return response.data.positions || [];
    } catch (error) {
      console.error('Error fetching latest positions:', error);
      throw error;
    }
  }

  /**
   * Fetch positions within a date range for a session
   */
  async getPositionsBySessionIdAndDateRange(
    sessionId: string,
    startDate: string,
    endDate: string
  ): Promise<Position[]> {
    try {
      const response = await apiClient.get<any>(
        `${this.endpoint}?sessionId=${sessionId}&since=${startDate}&until=${endDate}`
      );
      return response.data.positions || [];
    } catch (error) {
      console.error('Error fetching positions:', error);
      throw error;
    }
  }
}

export const positionsService = new PositionsService();
